// Вокабуляры eyeq — перечислимые значения домена, объявленные один раз.
// (vocab/plan.ts занят реестром локалей pothos, поэтому файл называется по
// проекту, а не по экрану.)
import { z } from 'zod';

/** Канал сбора ячейки. Ровно два, и они дизъюнктны ПО ПОСТРОЕНИЮ:
 *  cards = source_pipeline 'eyeq-intake' (карты / массовый интейк),
 *  people = всё остальное (участники). Третьего ведра нет — новый
 *  source_pipeline не может тихо выпасть из счёта. */
export const EYEQ_CELL_CHANNELS = ['people', 'cards'] as const;
export type EyeqCellChannel = (typeof EYEQ_CELL_CHANNELS)[number];

/** Маркер интейк-пайплайна в eyeq_assets.source_pipeline. Живёт здесь, а не в
 *  apps/eyeq-api/src/indexer.ts, чтобы cells.ts и фронт могли ссылаться на
 *  него без циклического импорта; indexer.ts реэкспортирует старое имя. */
export const EYEQ_INTAKE_PIPELINE = 'eyeq-intake';

/** Refusals of a blob replacement. Neither is a new asset status. */
export const EyeqBlobConflictReasonSchema = z.enum(['asset_locked', 'blob_conflict']);
// ─── Что именно на кадре ────────────────────────────────────────────────────
// Регламент сбора просит НЕ один кадр, а связку: сам объект плюс то, чем его
// можно опознать до конкретной модели — ценник, штрихкод, этикетка с
// названием. Приёмник (TG-бот, ADR-0082) берёт «всё подряд» и типа не
// выдумывает, поэтому вокабуляр обязан иметь честное «пока не разобрано».
//
//   unknown    — кадр принят, тип ещё не определён. ДЕФОЛТ на приёме.
//   target     — целевой кадр объекта: то, за чем шли.
//   identifier — опознаватель: ценник, штрихкод, этикетка, табличка с
//                моделью. Сам по себе как целевой кадр не сдаётся.
//
// Тип ставит НЕ приёмник: наличие читаемого штрихкода признаком опознавателя
// не является (у товара код напечатан на упаковке), а ценник часто вообще без
// кода — только цифры. Решают AI-категоризация и QA, а приёмник фиксирует то,
// из чего они смогут решить: связку сдачи и найденные коды.
export const EYEQ_SHOT_KINDS = ['unknown', 'target', 'identifier'] as const;
export type EyeqShotKind = (typeof EYEQ_SHOT_KINDS)[number];
export const EyeqShotKindSchema = z.enum(EYEQ_SHOT_KINDS);


/** Состояние закрытия ячейки плана — по УБЫВАНИЮ силы доказательства:
 *    client     — закрыта приёмкой заказчика (client_accepted ≥ план);
 *    shipped    — закрыта отгруженным, стоящим после вычета клиентских
 *                 реджектов (N ≥ план);
 *    shipped_qa — закрыта отгруженным + материалом в процессе (N + Q ≥ план);
 *    offline    — закрыта только с учётом материала конвейера, ещё не
 *                 залитого в R2 (N + Q + offline_pending ≥ план);
 *    open       — не закрыта ничем;
 *    ''         — ячейки нет в плане (нет строки квоты).
 *  Лестница монотонна: CA ⊆ N ⊆ N+Q ⊆ N+Q+OP, поэтому состояние называет
 *  СИЛЬНЕЙШЕЕ доказательство закрытия. Не хранится в D1 — чистая функция
 *  eyeqCellClosure (eyeq/plan.ts) от счётчиков реестра. */
export const EYEQ_CELL_CLOSURES = ['', 'open', 'offline', 'shipped_qa', 'shipped', 'client'] as const;
export type EyeqCellClosure = (typeof EYEQ_CELL_CLOSURES)[number];

/** Действия аудита eyeq_audit — реестр, чтобы точки записи, недельная лента
 *  ревьюера и таймлайн ассета не разъезжались в написании. Значение = то, что
 *  реально лежит в колонке action (включая легаси-бекфилл 0030). */
export const EYEQ_AUDIT_ACTIONS = [
  // per-asset (target = asset_id)
  'asset_submit',
  'asset_submit_legacy',
  'asset_url_set',
  'qa_accept',
  'qa_pii',
  'qa_reject',
  'qa_move',
  'qa_reject_frame',
  'qa_reject_url',
  'qa_clear',
  'link_attach',
  'link_answer',
  'link_candidate_reject',
  'client_verdict',
  'url_candidate_add',
  'url_suggest_maps',
  // batch / admin (target = shipment_id, cell, public_id или '')
  'ship',
  'ship_flagged',
  'delivery_register',
  'client_verdicts',
  'quota_move',
  'quota_policy',
  'pool_flags',
  'preview_ready',
  'dev_overlay',
  'cells_reconcile',
  'cat_id_realign',
  'intake_import',
  'intake_links',
  'link_assign',
  'member_provision',
  'invite_create',
  'invite_claim',
  'join',
  'credit',
] as const;
export type EyeqAuditAction = (typeof EYEQ_AUDIT_ACTIONS)[number];
